import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useRealtimeEvent } from '@/realtime/useRealtimeEvent';

import { NOTIFICATIONS_CHANGED_EVENT, notificationsApi, notificationsKeys } from '../notifications';

export function useNotificationsFeedQuery() {
  return useInfiniteQuery({
    queryKey: notificationsKeys.feed,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => notificationsApi.feed(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: last => last.nextCursor ?? undefined,
  });
}

export function useUnreadNotificationsCountQuery() {
  return useQuery({ queryKey: notificationsKeys.unreadCount, queryFn: () => notificationsApi.unreadCount() });
}

export function useMarkNotificationsReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids: string[] } | { all: true }) => notificationsApi.markRead(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKeys.all }),
  });
}

export function useMarkNotificationUnreadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markUnread(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKeys.all }),
  });
}

export function useClearNotificationsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.clearAll(),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKeys.all }),
  });
}

export function useSeenPathMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (path: string) => notificationsApi.seenPath(path),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKeys.all }),
  });
}

/** One subscription point for the bell: any realtime ping refetches whatever notifications data is cached. */
export function useNotificationsRealtime(): void {
  const queryClient = useQueryClient();
  const onChanged = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
  }, [queryClient]);

  useRealtimeEvent(NOTIFICATIONS_CHANGED_EVENT, onChanged);
}

export function usePendingToastsQuery() {
  return useQuery({ queryKey: notificationsKeys.pendingToasts, queryFn: () => notificationsApi.pendingToasts() });
}
